<template>
    <!-- This example requires Tailwind CSS v2.0+ -->
    <div
        class="group mb-4 rounded-lg border-iron-200 px-2 py-2.5 shadow-sm ring-iron-200 sm:px-4 sm:py-5 w-full ring-1 transition-all duration-200"
        :class="{
            'bg-white hover:shadow-lg hover:ring-z-primary-400 cursor-pointer hover:ring-2':
                eventType.enabled_at,
            'bg-zinc-100': !eventType.enabled_at,
        }"
        @click="goToEventType"
    >
        <div class="flex justify-between">
            <div class="gap-2 flex items-start">
                <div class="pt-2">
                    <div
                        class="h-3 w-3 shrink-0 rounded-full"
                        :class="statusDotClasses"
                    ></div>
                </div>

                <div class="min-w-0 flex-1">
                    <div class="gap-1.5 flex items-center">
                        <p
                            class="p-1 font-medium"
                            :class="{
                                'text-iron-900': eventType.enabled_at,
                                'text-iron-400': !eventType.enabled_at,
                            }"
                        >
                            {{ eventType.name }}
                        </p>
                        <Popover
                            v-if="hasWarnings && eventType.enabled_at"
                            v-model:open="warningPopoverOpen"
                        >
                            <PopoverTrigger
                                as-child
                                @click.stop
                                @mouseenter="openWarningPopover"
                                @mouseleave="closeWarningPopoverWithDelay"
                            >
                                <button class="flex items-center">
                                    <AlertCircle
                                        class="text-amber-500 hover:text-amber-600 shrink-0 cursor-pointer transition-colors"
                                        :size="20"
                                    />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                class="w-72 p-0"
                                :side-offset="10"
                                @click.stop
                                @mouseenter="cancelCloseWarningPopover"
                                @mouseleave="warningPopoverOpen = false"
                            >
                                <div class="border-iron-100 px-4 py-3 border-b">
                                    <p class="font-medium text-iron-900">
                                        {{
                                            $t(
                                                "event_types.warnings.incomplete_configuration",
                                            )
                                        }}
                                    </p>
                                </div>
                                <div class="divide-iron-100 divide-y">
                                    <InertiaLink
                                        v-for="warning in warningDetails"
                                        :key="warning.key"
                                        :href="getWarningUrl(warning.tab)"
                                        class="gap-3 px-4 py-3 hover:bg-iron-50 flex items-center justify-between transition-colors"
                                    >
                                        <div class="min-w-0 flex-1">
                                            <p class="text-sm text-iron-600">
                                                {{ warning.message }}
                                            </p>
                                            <p
                                                class="mt-1 text-sm font-medium text-z-primary-600"
                                            >
                                                {{ warning.cta }}
                                            </p>
                                        </div>
                                        <ArrowRight
                                            class="text-iron-400 shrink-0"
                                            :size="16"
                                        />
                                    </InertiaLink>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>

                    <!-- <PreviewPublicUrlCopy
                        class="hidden sm:flex"
                        :event-type="eventType"
                    /> -->

                    <div class="gap-2 flex">
                        <div
                            v-if="eventType.duration !== null"
                            class="mt-2 gap-1 flex items-center"
                        >
                            <Clock class="text-iron-400" :size="20" />
                            <span class="text-xs font-medium text-iron-500"
                                >{{ eventType.duration }} min</span
                            >
                        </div>

                        <div
                            v-if="
                                eventType.stripe_config &&
                                eventType.stripe_config.enabled
                            "
                            class="mt-2 gap-1 flex items-center"
                        >
                            <CircleDollarSign
                                class="text-iron-400"
                                :size="20"
                            />
                            <span class="text-xs font-medium text-iron-500"
                                >{{ eventType.stripe_config.price / 100 }}
                                {{ eventType.stripe_config.currency }}
                            </span>
                        </div>

                        <div
                            v-if="primaryLocation"
                            class="mt-2 gap-1 min-w-0 flex items-center"
                        >
                            <div
                                class="h-5 w-5 text-iron-400 shrink-0"
                                v-html="primaryLocation.icon"
                            ></div>
                            <span
                                class="text-xs font-medium text-iron-500 truncate"
                                :title="primaryLocation.label"
                                >{{ primaryLocation.label }}</span
                            >
                        </div>
                    </div>
                </div>
            </div>
            <div class="sm:mt-0 md:justify-start flex justify-end">
                <div class="inline-flex">
                    <a
                        v-if="eventType.enabled_at"
                        target="_blank"
                        :href="eventTypeUrl"
                        class="gap-2 rounded-l-md bg-white p-2 text-sm font-medium text-iron-900 ring-iron-300 hover:bg-iron-50 relative inline-flex max-h-[36px] cursor-pointer items-center ring-1 ring-inset focus:z-10"
                        @click.stop
                    >
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Eye class="text-iron-500" :size="20" />
                                </TooltipTrigger>
                                <TooltipContent :side-offset="10">
                                    <p>
                                        {{
                                            $t(
                                                "event_types.event_preview.open_booking_page",
                                            )
                                        }}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </a>

                    <button
                        v-if="isSupported && eventType.enabled_at"
                        type="button"
                        class="bg-white p-2 text-sm font-medium ring-iron-300 hover:bg-iron-50 relative -ml-px inline-flex max-h-[36px] items-center ring-1 ring-inset focus:z-10"
                        @click.stop="copyEventTypePreviewToClipboard"
                    >
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Transition mode="out-in">
                                        <Link
                                            v-if="!copied"
                                            class="text-iron-500"
                                            :size="20"
                                        />
                                        <ClipboardCheck
                                            v-else
                                            class="text-iron-500"
                                            :size="20"
                                        />
                                    </Transition>
                                </TooltipTrigger>
                                <TooltipContent :side-offset="10">
                                    <p>
                                        {{
                                            $t(
                                                "event_types.event_preview.copy_url",
                                            )
                                        }}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </button>

                    <ShareHubModal
                        v-if="eventType.enabled_at"
                        v-model:open="shareHubOpen"
                        :event-type="eventType"
                    >
                        <button
                            type="button"
                            class="bg-white p-2 text-sm font-medium ring-iron-300 hover:bg-iron-50 relative -ml-px inline-flex max-h-[36px] items-center ring-1 ring-inset focus:z-10"
                            @click.stop
                        >
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Share
                                            class="text-iron-500"
                                            :size="18"
                                        />
                                    </TooltipTrigger>
                                    <TooltipContent :side-offset="10">
                                        <p>
                                            {{
                                                $t(
                                                    "event_types.event_preview.share",
                                                )
                                            }}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </button>
                    </ShareHubModal>

                    <button
                        v-if="!eventType.enabled_at"
                        type="button"
                        class="mr-3 rounded-l-md rounded-r-md bg-white py-3.5 text-sm font-medium ring-iron-300 hover:bg-iron-50 relative inline-flex max-h-[36px] items-center ring-1 ring-inset focus:z-10"
                    >
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Transition mode="out-in">
                                        <button
                                            class="text-iron-600 flex"
                                            @click.stop="enableEventType"
                                        >
                                            <span class="px-2">
                                                <Zap :size="20" />
                                            </span>
                                            <span class="pr-3">{{
                                                $t(
                                                    "event_types.event_preview.enable",
                                                )
                                            }}</span>
                                        </button>
                                    </Transition>
                                </TooltipTrigger>
                                <TooltipContent :side-offset="10">
                                    <p>
                                        {{
                                            $t(
                                                "event_types.event_preview.enable",
                                            )
                                        }}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </button>

                    <Menu as="div" class="relative -ml-px block">
                        <MenuButton
                            class="rounded-r-md bg-white p-2 text-iron-400 ring-iron-300 hover:bg-iron-50 relative inline-flex items-center ring-1 ring-inset focus:z-10"
                            :class="{
                                'rounded-l-md': !eventType.enabled_at,
                            }"
                            @click.stop
                        >
                            <span class="sr-only">Open options</span>
                            <MoreVertical class="text-iron-500" :size="20" />
                        </MenuButton>
                        <transition
                            enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95"
                        >
                            <MenuItems
                                class="right-0 -mr-1 mt-2 w-56 rounded-md bg-white shadow-lg ring-black ring-opacity-5 absolute z-10 origin-top-right ring-1 focus:outline-none"
                            >
                                <MenuItem
                                    v-for="item in items"
                                    :key="item.name"
                                    v-slot="{ active }"
                                >
                                    <div>
                                        <div
                                            v-if="item.isCopied?.value === true"
                                            class="gap-2.5 px-4 py-2.5 text-sm font-medium flex w-full items-center"
                                        >
                                            <CopyCheck
                                                :size="20"
                                                class="text-iron-500"
                                            />
                                            {{ $t("copied") }}
                                        </div>

                                        <component
                                            v-else
                                            :is="
                                                item.href
                                                    ? 'InertiaLink'
                                                    : 'button'
                                            "
                                            :href="item.href || undefined"
                                            class="gap-2.5 px-4 py-2.5 text-sm font-medium flex w-full items-center"
                                            :class="[
                                                active && item.type === 'danger'
                                                    ? 'bg-error-25'
                                                    : active
                                                      ? 'bg-iron-50 text-iron-900'
                                                      : 'text-iron-700',
                                                item.type === 'danger'
                                                    ? 'text-error-500'
                                                    : 'text-iron-500',
                                            ]"
                                            @click.stop="
                                                item.onClick
                                                    ? item.onClick()
                                                    : () => {}
                                            "
                                        >
                                            <component
                                                :is="item.icon"
                                                :class="[
                                                    item.type === 'danger'
                                                        ? 'text-error-500'
                                                        : 'text-iron-500',
                                                ]"
                                                :size="20"
                                            ></component>
                                            {{ item.name }}
                                        </component>
                                    </div>
                                </MenuItem>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
            </div>
        </div>

        <!-- QR Code Dialog for Desktop (triggered from menu) -->
        <QrCodeShareDialog
            v-if="eventType.enabled_at"
            v-model:open="qrDialogOpen"
            :event-type-id="eventType.id"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { router } from "@inertiajs/vue3";
import useConfirm from "../ConfirmDialog/composables/useConfirm";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import route from "ziggy-js";
import { useClipboard } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import IntegrationCodeModal from "./components/IntegrationCodeModal.vue";
import QrCodeShareDialog from "./components/QrCodeShareDialog.vue";
import { ShareHubModal } from "./components/ShareHub";
import type { EventType } from "./types/EventType";

import {
    Eye,
    Link,
    MoreVertical,
    Pencil,
    Code,
    Trash,
    ClipboardCheck,
    Clock,
    CopyPlus,
    ZapOff,
    Zap,
    CircleDollarSign,
    KeyRound,
    CopyCheck,
    QrCode as QrCodeIcon,
    AlertCircle,
    ArrowRight,
    Share,
} from "lucide-vue-next";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Link as InertiaLink } from "@inertiajs/vue3";
import { useHasSubscription } from "@/composables/useHasSubscription";
import useProDialog from "@/ProModal/composables/useProDialog";
import { getEventTypeStatusDotClasses } from "./utils/getEventTypeStatusDotClasses";
import { useLocationOptions } from "./useLocationOptions";

const props = defineProps<{
    eventType: EventType;
}>();

// Use the backend-computed warnings array
const configurationWarnings = computed(
    () => props.eventType.configurationWarnings ?? [],
);
const hasWarnings = computed(() => configurationWarnings.value.length > 0);
const statusDotClasses = computed(() =>
    getEventTypeStatusDotClasses({
        enabled: Boolean(props.eventType.enabled_at),
        hasWarnings: hasWarnings.value,
    }),
);

type MenuItemType = {
    name: string;
    href?: string;
    onClick?: () => void;
    icon: any;
    type?: "danger";
    isCopied?: ComputedRef<boolean>;
};

const target = ref(null);
const opened = ref(false);
const qrDialogOpen = ref(false);
const shareHubOpen = ref(false);
const warningPopoverOpen = ref(false);
let warningPopoverTimeout: ReturnType<typeof setTimeout> | null = null;

function openWarningPopover() {
    if (warningPopoverTimeout) {
        clearTimeout(warningPopoverTimeout);
        warningPopoverTimeout = null;
    }
    warningPopoverOpen.value = true;
}

function closeWarningPopoverWithDelay() {
    warningPopoverTimeout = setTimeout(() => {
        warningPopoverOpen.value = false;
    }, 100);
}

function cancelCloseWarningPopover() {
    if (warningPopoverTimeout) {
        clearTimeout(warningPopoverTimeout);
        warningPopoverTimeout = null;
    }
}

const { show } = useConfirm();

const { copy, copied, isSupported } = useClipboard();
const { hasSubscription } = useHasSubscription();

const proDialog = useProDialog();

const { t } = useI18n();

type WarningDetail = {
    key: string;
    message: string;
    tab: string;
    cta: string;
};

const warningDetails = computed<WarningDetail[]>(() => {
    const warningConfig: Record<
        string,
        { message: string; tab: string; cta: string }
    > = {
        no_location: {
            message: t("event_types.warnings.no_location"),
            tab: "booking-page",
            cta: t("event_types.warnings.cta.configure_location"),
        },
        no_team_users: {
            message: t("event_types.warnings.no_team_users"),
            tab: "team",
            cta: t("event_types.warnings.cta.assign_members"),
        },
        no_availability: {
            message: t("event_types.warnings.no_availability"),
            tab: "availability",
            cta: t("event_types.warnings.cta.set_availability"),
        },
        no_calendar_destination: {
            message: t("event_types.warnings.no_calendar_destination"),
            tab: "calendars-and-hosts",
            cta: t("event_types.warnings.cta.select_calendar"),
        },
    };
    return configurationWarnings.value.map((key) => ({
        key,
        ...(warningConfig[key] ?? {
            message: key,
            tab: "general",
            cta: t("event_types.warnings.cta.fix_issue"),
        }),
    }));
});

function getWarningUrl(tab: string): string {
    return (
        route("app.event-types.show", { eventType: props.eventType.id }) +
        `?tab=${tab}`
    );
}

onClickOutside(target, () => (opened.value = false));

function openQrCodeDialog() {
    qrDialogOpen.value = true;
}

const removeEventType = async () => {
    const result = await show({
        header: `
            ${t("event_types.event_preview.remove_detail.title")}: ${
                props.eventType.name
            }?`,
        message: "",
        confirm: t("event_types.event_preview.remove_detail.remove_event_type"),
        cancel: t("cancel"),
    });

    if (result) {
        await router.delete(`event-types/${props.eventType.id}`);
    }
};

const eventTypeUrl = computed(() => {
    return props.eventType.public_url;
});

const { resolveLocation } = useLocationOptions();

const primaryLocation = computed(() => {
    const location = props.eventType.locations?.[0];
    return location ? resolveLocation(location) : null;
});

function copyEventTypePreviewToClipboard() {
    copy(eventTypeUrl.value);
}

function copyTokenToClipboard() {
    copy(props.eventType.tokens[0].token);
}

async function duplicateEventType() {
    if (!hasSubscription) {
        proDialog.show({
            feature: "unlimited_events",
        });
    } else {
        await router.post(
            route("app.event-types.duplicate", {
                eventType: props.eventType.id,
            }),
        );
    }
}

function goToEventType() {
    if (!props.eventType.enabled_at) {
        return;
    }

    router.visit(
        route("app.event-types.show", {
            eventType: props.eventType.id,
        }),
    );
}

async function enableEventType() {
    const result = await show({
        header: `
            ${t("event_types.event_preview.enable_detail.title")}: ${
                props.eventType.name
            }?`,
        message: t("event_types.event_preview.enable_detail.description"),
        confirm: t("event_types.event_preview.enable"),
        cancel: t("cancel"),
    });

    if (result) {
        await router.post(
            route("app.event-types.enable", { eventType: props.eventType.id }),
        );
    }
}

async function disableEventType() {
    const result = await show({
        header: `
            ${t("event_types.event_preview.disable_detail.title")}: ${
                props.eventType.name
            }?`,
        message: t("event_types.event_preview.disable_detail.description"),
        confirm: t(
            "event_types.event_preview.disable_detail.disable_event_type",
        ),
        cancel: t("cancel"),
    });

    if (result) {
        await router.post(
            route("app.event-types.disable", { eventType: props.eventType.id }),
        );
    }
}

const items = computed((): MenuItemType[] => {
    return [
        {
            name: t("event_types.event_preview.configure"),
            href: route("app.event-types.show", {
                eventType: props.eventType.id,
            }),
            icon: Pencil,
        },
        {
            name: t("event_types.event_preview.duplicate"),
            onClick: duplicateEventType,
            icon: CopyPlus,
        },

        {
            name: t("event_types.event_preview.copy_token"),
            onClick: copyTokenToClipboard,
            icon: KeyRound,
            isCopied: copied,
        },
        {
            name: props.eventType.enabled_at
                ? t("event_types.event_preview.disable")
                : t("event_types.event_preview.enable"),
            onClick: props.eventType.enabled_at
                ? disableEventType
                : enableEventType,
            icon: props.eventType.enabled_at ? ZapOff : Zap,
        },
        {
            name: t("event_types.event_preview.remove"),
            onClick: removeEventType,
            icon: Trash,
            type: "danger",
        },
    ];
});
</script>

<style>
@import url("/node_modules/@zaptime/vue3/dist/style.css");
</style>
